import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ImageDown, Loader2, Mail, Plus, Printer, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import {
  Card, Spinner, StatusBadge, btnPrimary, fmtDay, gbp2, inputCls,
  SIMPLE_ORDER_STATUSES, orderStatusFor, simpleOrderStatus,
} from "@/components/portal/shared";
import { PhotoUpload } from "@/components/portal/PhotoUpload";

const uid = () => Math.random().toString(36).slice(2, 9);
const BLANK_WIN = { ref: "", blindType: "", width: "", drop: "", unit: "mm", controlSide: "", material: "", sizeType: "Recess", qty: 1, price: "" };
const cell = "rounded-lg border border-warm-300 px-2 py-2 text-xs focus:border-navy-900 focus:outline-none w-full min-w-14";

export default function OrderDetailPage() {
  const { id } = useParams();
  const { api, user } = usePortalAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);
  const [team, setTeam] = useState([]);
  const [invoiceId, setInvoiceId] = useState(null);
  const [form, setForm] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [emailTo, setEmailTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [jpegBusy, setJpegBusy] = useState(false);
  const snapshotRef = useRef(null);

  const load = async () => {
    const [{ data: o }, inv] = await Promise.all([
      api.get(`/orders/${id}`),
      api.get("/invoices").catch(() => ({ data: { invoices: [] } })),
    ]);
    setOrder(o);
    setInvoiceId((inv.data.invoices.find((i) => i.order_id === o.id) || {}).id || null);
    setForm({
      status: simpleOrderStatus(o.status),
      sales_rep: o.sales_rep || "",
      payment_method: o.payment_method || "",
      install_date: o.install_date || "",
      install_time: o.install_time || "",
      total: o.total ?? "",
      deposit_required: o.deposit_required ?? "",
      deposit_paid: o.deposit_paid ?? 0,
      balance_paid: o.balance_paid ?? 0,
      deposit_method: o.deposit_method || "Bank transfer",
      balance_method: o.balance_method || "Bank transfer",
    });
    setRooms(
      o.lead?.measurement_rooms?.length
        ? o.lead.measurement_rooms
        : [{ id: uid(), name: "Room 1", windows: [{ id: uid(), ...BLANK_WIN }] }],
    );
    const fitter = (o.installer || "").toLowerCase();
    if (fitter) {
      api.get("/team").then((r) => {
        const match = r.data.users.find((u) => u.name.toLowerCase() === fitter);
        if (match) setEmailTo(match.email);
      }).catch(() => {});
    }
  };

  useEffect(() => {
    if (user === false) { navigate("/portal/login", { replace: true }); return; }
    if (user) {
      load().catch(() => setMissing(true));
      api.get("/team").then((r) => setTeam(r.data.users)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const flatWindows = useMemo(
    () => rooms.flatMap((r, ri) => r.windows.map((w, wi) => ({ r, ri, w, wi }))),
    [rooms],
  );

  if (missing) return <p className="text-sm text-red-700" role="alert">Order not found.</p>;
  if (!order || !form) return <Spinner />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const paid = (Number(form.deposit_paid) || 0) + (Number(form.balance_paid) || 0);
  const balanceDue = Math.max(0, (Number(form.total) || 0) - paid);
  const leadId = order.lead?.id;

  const setWin = (ri, wi, k, v) =>
    setRooms((rs) => rs.map((r, i) => (i !== ri ? r : { ...r, windows: r.windows.map((w, x) => (x === wi ? { ...w, [k]: v } : w)) })));
  const addWindow = () =>
    setRooms((rs) => {
      const last = rs.length - 1;
      return rs.map((r, i) => (i === last ? { ...r, windows: [...r.windows, { id: uid(), ...BLANK_WIN }] } : r));
    });
  const delWindow = (ri, wi) =>
    setRooms((rs) => rs.map((r, i) => (i !== ri ? r : { ...r, windows: r.windows.filter((_, x) => x !== wi) })).filter((r) => r.windows.length > 0));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        sales_rep: form.sales_rep,
        payment_method: form.payment_method,
        install_date: form.install_date,
        install_time: form.install_time,
        total: Number(form.total) || 0,
        deposit_required: Number(form.deposit_required) || 0,
        deposit_paid: Number(form.deposit_paid) || 0,
        balance_paid: Number(form.balance_paid) || 0,
        deposit_method: form.deposit_method,
        balance_method: form.balance_method,
      };
      // Only send status when it actually changed — payment auto-promotions must win otherwise
      if (form.status !== simpleOrderStatus(order.status)) payload.status = orderStatusFor(form.status);
      await api.patch(`/orders/${id}`, payload);
      if (leadId) await api.put(`/leads/${leadId}/measurements`, { rooms });
      toast.success("Order & measurements saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveMeasurements = async () => {
    setBusy(true);
    try {
      await api.put(`/leads/${leadId}/measurements`, { rooms });
      toast.success("Measurements saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveJpeg = async () => {
    if (jpegBusy || !snapshotRef.current) return;
    setJpegBusy(true);
    try {
      const canvas = await html2canvas(snapshotRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/jpeg", 0.92);
      a.download = `${order.ref}-measurements.jpg`;
      a.click();
      toast.success("Measurements saved as a JPEG — ready to send to the fitter");
    } catch {
      toast.error("Could not create the image — try again");
    } finally {
      setJpegBusy(false);
    }
  };

  const emailSheet = async () => {
    if (!emailTo.trim()) { toast.error("Enter an email address first"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${id}/email-sheet`, { email: emailTo.trim() });
      toast.success(`Order sheet emailed to ${emailTo.trim()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteOrder = async () => {
    setBusy(true);
    try {
      await api.delete(`/orders/${id}`);
      toast.success(`Order ${order.ref} deleted`);
      navigate("/portal/orders");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div data-testid="order-detail" className="space-y-5">
      <Link to="/portal/orders" className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 hover:text-navy-800" data-testid="order-back">
        <ArrowLeft className="h-4 w-4" /> Orders
      </Link>

      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl text-navy-900" data-testid="order-ref">{order.ref}</h1>
          <p className="text-xs text-slate-500 mt-1">
            <Link to={`/portal/customers/${leadId}`} className="hover:underline">{order.customer}</Link>
            {" · "}{[order.address1, order.town, order.postcode].filter(Boolean).join(", ") || "no address"}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <StatusBadge s={order.status} />
          <Link to={`/portal/orders/${id}/sheet`} data-testid="order-sheet-btn" className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}>
            <Printer className="h-3.5 w-3.5" /> PDF
          </Link>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" data-testid="order-delete" className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-red-200 text-red-700 hover:bg-red-50 bg-white")}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete order {order.ref}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the order and any linked invoices. The customer record and their measurements stay on file. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="order-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="order-delete-confirm" onClick={deleteOrder} className="bg-red-700 hover:bg-red-800">Delete order</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button type="button" data-testid="order-save" onClick={save} disabled={busy} className={`${btnPrimary} !bg-red-700 hover:!bg-red-800`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5">
          <Card title="Order Details" testid="order-details-card">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-status">Status</label>
                <select id="od-status" data-testid="order-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                  {SIMPLE_ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-rep">Sales Rep</label>
                <select id="od-rep" data-testid="order-rep-select" value={form.sales_rep} onChange={(e) => set("sales_rep", e.target.value)} className={inputCls}>
                  <option value="">Select…</option>
                  {team.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-paymethod">Payment Method</label>
                <select id="od-paymethod" data-testid="order-paymethod-select" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className={inputCls}>
                  <option value="">Select…</option>
                  <option>Bank transfer</option>
                  <option>Card</option>
                  <option>Cash</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-install-date">Installation Date</label>
                <input id="od-install-date" type="date" data-testid="order-install-date" value={form.install_date} onChange={(e) => set("install_date", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-install-time">Installation Time</label>
                <input id="od-install-time" type="text" placeholder="e.g. 10:00" data-testid="order-install-time" value={form.install_time} onChange={(e) => set("install_time", e.target.value)} className={inputCls} />
              </div>
            </div>
          </Card>

          <Card title={`Measurements (${flatWindows.length})`} testid="order-measurements-card"
            action={<button type="button" data-testid="order-add-window" onClick={addWindow} className={btnPrimary}><Plus className="h-3.5 w-3.5" /> Add Window</button>}>
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-white bg-navy-950">
                    <th className="px-2.5 py-2 rounded-tl-lg">No</th>
                    <th className="px-2.5 py-2">Window / Room</th>
                    <th className="px-2.5 py-2">Width</th>
                    <th className="px-2.5 py-2">Drop</th>
                    <th className="px-2.5 py-2">Cntrl</th>
                    <th className="px-2.5 py-2">Unit</th>
                    <th className="px-2.5 py-2">Material</th>
                    <th className="px-2.5 py-2">R/S</th>
                    <th className="px-2.5 py-2 rounded-tr-lg"></th>
                  </tr>
                </thead>
                <tbody>
                  {flatWindows.map(({ r, ri, w, wi }, idx) => (
                    <tr key={w.id} className="border-b border-warm-200/60" data-testid={`measure-row-${idx}`}>
                      <td className="px-2.5 py-1.5 text-xs text-slate-400">{idx + 1}</td>
                      <td className="px-1.5 py-1.5"><input aria-label="Window or room" data-testid={`measure-room-${idx}`} value={w.ref || ""} placeholder={r.name} onChange={(e) => setWin(ri, wi, "ref", e.target.value)} className={cell} /></td>
                      <td className="px-1.5 py-1.5"><input aria-label="Width" data-testid={`measure-width-${idx}`} value={w.width} onChange={(e) => setWin(ri, wi, "width", e.target.value)} className={cell} /></td>
                      <td className="px-1.5 py-1.5"><input aria-label="Drop" data-testid={`measure-drop-${idx}`} value={w.drop} onChange={(e) => setWin(ri, wi, "drop", e.target.value)} className={cell} /></td>
                      <td className="px-1.5 py-1.5">
                        <select aria-label="Control side" data-testid={`measure-control-${idx}`} value={w.controlSide} onChange={(e) => setWin(ri, wi, "controlSide", e.target.value)} className={cell}>
                          <option value="">—</option><option>RW</option><option>LW</option>
                        </select>
                      </td>
                      <td className="px-1.5 py-1.5">
                        <select aria-label="Unit" data-testid={`measure-unit-${idx}`} value={w.unit} onChange={(e) => setWin(ri, wi, "unit", e.target.value)} className={cell}>
                          <option>mm</option><option>cm</option><option>inches</option>
                        </select>
                      </td>
                      <td className="px-1.5 py-1.5"><input aria-label="Material" data-testid={`measure-material-${idx}`} value={w.material} onChange={(e) => setWin(ri, wi, "material", e.target.value)} className={cell} /></td>
                      <td className="px-1.5 py-1.5">
                        <select aria-label="Recess or exact" data-testid={`measure-fitting-${idx}`} value={w.sizeType} onChange={(e) => setWin(ri, wi, "sizeType", e.target.value)} className={cell}>
                          <option>Recess</option><option>Exact</option>
                        </select>
                      </td>
                      <td className="px-1.5 py-1.5">
                        <button type="button" aria-label="Delete row" data-testid={`measure-del-${idx}`} onClick={() => delWindow(ri, wi)} className="text-slate-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-[11px] text-slate-400">Measurements save to the customer record too.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="measure-save-jpeg"
                  onClick={saveJpeg}
                  disabled={jpegBusy}
                  className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}
                >
                  {jpegBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageDown className="h-3.5 w-3.5" />} Save as JPEG
                </button>
                <button type="button" data-testid="order-save-measurements" onClick={saveMeasurements} disabled={busy} className={btnPrimary}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Measurements
                </button>
              </div>
            </div>

            {/* Off-screen snapshot used for the JPEG export (plain text so the image is always clean) */}
            <div ref={snapshotRef} aria-hidden="true" style={{ position: "fixed", left: -9999, top: 0, width: 720, background: "#ffffff", fontFamily: "Georgia, serif" }}>
              <div style={{ background: "#0B1D3A", color: "#ffffff", padding: "20px 28px" }}>
                <p style={{ margin: 0, fontSize: 20, letterSpacing: 1 }}>My Blinds Company</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.7 }}>Order sheet — measurements &amp; fitting address</p>
              </div>
              <div style={{ padding: "22px 28px" }}>
                <p style={{ margin: 0, fontSize: 15, color: "#0B1D3A" }}>
                  <strong>{order.ref}</strong> — {order.customer}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
                  {[order.address1, order.town, order.postcode].filter(Boolean).join(", ") || "No address on file"}
                  {order.phone ? ` · ${order.phone}` : ""}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
                  Fitting: {[form.install_date ? fmtDay(form.install_date) : "", form.install_time, order.installer].filter(Boolean).join(" · ") || "to be booked"}
                </p>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14, fontSize: 12, fontFamily: "Arial, sans-serif" }}>
                  <thead>
                    <tr style={{ background: "#0B1D3A", color: "#ffffff" }}>
                      {["No", "Window / Room", "Width", "Drop", "Cntrl", "Unit", "Material", "R/S"].map((h) => (
                        <th key={h} style={{ padding: "7px 8px", textAlign: h === "Window / Room" || h === "Material" ? "left" : "center", fontSize: 10, letterSpacing: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flatWindows.map(({ r, w }, idx) => (
                      <tr key={w.id} style={{ borderBottom: "1px solid #E7E2D8" }}>
                        <td style={{ padding: "7px 8px", color: "#94A3B8" }}>{idx + 1}</td>
                        <td style={{ padding: "7px 8px", color: "#1E293B" }}>{w.ref || r.name}</td>
                        <td style={{ padding: "7px 8px", textAlign: "center" }}>{w.width}</td>
                        <td style={{ padding: "7px 8px", textAlign: "center" }}>{w.drop}</td>
                        <td style={{ padding: "7px 8px", textAlign: "center" }}>{w.controlSide || "—"}</td>
                        <td style={{ padding: "7px 8px", textAlign: "center" }}>{w.unit}</td>
                        <td style={{ padding: "7px 8px" }}>{w.material || "—"}</td>
                        <td style={{ padding: "7px 8px", textAlign: "center" }}>{w.sizeType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ margin: "14px 0 0", fontSize: 13, color: "#0B1D3A", fontFamily: "Arial, sans-serif" }}>
                  <strong>Order total: {gbp2(Number(form.total) || 0)}</strong>
                </p>
                <p style={{ margin: "10px 0 0", fontSize: 10, color: "#94A3B8", fontFamily: "Arial, sans-serif" }}>
                  My Blinds Company · Unit 13d, 47 Whitmore Road, Small Heath, Birmingham B10 0NT · 07868 295300
                </p>
              </div>
            </div>
          </Card>

          <Card title="Photos & Documents" testid="order-photos-card">
            <PhotoUpload
              api={api}
              attachUrl={`/orders/${id}/photos`}
              photos={order.photos || []}
              onChange={load}
              tags={["Survey", "Before", "During", "After", "Problem", "Other"]}
            />
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Financials" testid="order-financials-card">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-total">Total Price (£)</label>
                <input id="od-total" type="number" min="0" step="0.01" data-testid="order-total" value={form.total} onChange={(e) => set("total", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-depreq">Deposit Req. (£)</label>
                <input id="od-depreq" type="number" min="0" step="0.01" data-testid="order-deposit-req" value={form.deposit_required} onChange={(e) => set("deposit_required", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-deppaid">Deposit Paid (£)</label>
                <input id="od-deppaid" type="number" min="0" step="0.01" data-testid="order-deposit-paid" value={form.deposit_paid} onChange={(e) => set("deposit_paid", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-balpaid">Balance Paid (£)</label>
                <input id="od-balpaid" type="number" min="0" step="0.01" data-testid="order-balance-paid" value={form.balance_paid} onChange={(e) => set("balance_paid", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-warm-200 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-500"><span>Total</span><span data-testid="order-fin-total">{gbp2(Number(form.total) || 0)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Paid</span><span data-testid="order-fin-paid">{gbp2(paid)}</span></div>
              <div className="flex justify-between font-semibold text-red-700"><span>Balance Due</span><span data-testid="order-fin-due">{gbp2(balanceDue)}</span></div>
            </div>
          </Card>

          <Card title="Actions" testid="order-actions-card">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1.5" htmlFor="od-sheet-email">Email order sheet to</label>
                <div className="flex gap-2">
                  <input id="od-sheet-email" type="email" placeholder="fitter@email.com" data-testid="order-sheet-email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className={inputCls} />
                  <button type="button" data-testid="order-email-sheet" onClick={emailSheet} disabled={busy} className={`${btnPrimary} shrink-0`}><Mail className="h-3.5 w-3.5" /> Send</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">Sends items + measurements to the fitter.</p>
              </div>
              {invoiceId && (
                <Link to={`/portal/invoices/${invoiceId}/print`} data-testid="order-invoice-btn" className={`${btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")} w-full`}>
                  <Printer className="h-3.5 w-3.5" /> View Invoice
                </Link>
              )}
              {order.status !== "Installed" && order.status !== "Completed" && order.status !== "Balance Paid" && (
                <button
                  type="button"
                  data-testid="order-mark-installed"
                  onClick={async () => { await api.patch(`/orders/${id}`, { status: "Installed" }); toast.success("Marked as installed"); await load(); }}
                  className={`${btnPrimary} w-full`}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark Installed
                </button>
              )}
              {order.completion_signature && (
                <p className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-xs text-emerald-900 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Signed by {order.completion_name} on {fmtDay(order.installed_at)}
                </p>
              )}
            </div>
          </Card>

          <Card title="Payments" testid="order-payments-card">
            {paid === 0 ? (
              <p className="text-sm text-slate-400">None yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {Number(form.deposit_paid) > 0 && (
                  <li className="flex justify-between rounded-xl bg-warm-100 px-4 py-2.5">
                    <span>Deposit{order.deposit_method ? ` · ${order.deposit_method}` : ""}</span>
                    <span className="font-semibold">{gbp2(form.deposit_paid)}</span>
                  </li>
                )}
                {Number(form.balance_paid) > 0 && (
                  <li className="flex justify-between rounded-xl bg-warm-100 px-4 py-2.5">
                    <span>Balance{order.balance_method ? ` · ${order.balance_method}` : ""}</span>
                    <span className="font-semibold">{gbp2(form.balance_paid)}</span>
                  </li>
                )}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
